/**
 * Parse the `/favorites.php` page header to extract the user's 10 native
 * favorite categories (their custom names + per-category gallery counts).
 *
 * EhViewer renders this as a `<div class="nosel">` containing 10 `<div class="fp">`
 * blocks. Each block has:
 *   - the colored swatch (we ignore)
 *   - `<i>N</i>` — the count
 *   - the plain-text category name
 *
 * If the layout changes, the helper falls back to an unstyled 10-slot list
 * with the source's default names ("Favorites 0".."Favorites 9").
 */

import { parseDocument } from '@/lib/html';

export interface FavoritesHeader {
  /** Exactly 10 entries, one per favcat 0..9. */
  categories: Array<{ index: number; name: string; count: number }>;
}

const DEFAULT_NAMES = Object.freeze(
  Array.from({ length: 10 }, (_, i) => `Favorites ${i}`),
);

export function parseFavoritesHeader(html: string): FavoritesHeader {
  const result: FavoritesHeader = {
    categories: Array.from({ length: 10 }, (_, i) => ({
      index: i,
      name: DEFAULT_NAMES[i] ?? `Favorites ${i}`,
      count: 0,
    })),
  };
  if (!html) return result;
  const doc = parseDocument(html);

  // Primary layout: the "color picker" select on the favorites page lists
  // every category with its (user-customized) name as the option text.
  // `<select name="favcat"><option value="0">[fav 0]</option>...`.
  const select = doc.querySelector('select[name="favcat"]');
  if (select) {
    const options = Array.from(select.querySelectorAll('option')) as HTMLOptionElement[];
    for (const opt of options) {
      const value = opt.getAttribute('value') ?? '';
      const idx = Number.parseInt(value, 10);
      if (!Number.isFinite(idx) || idx < 0 || idx > 9) continue;
      const slot = result.categories[idx];
      if (!slot) continue;
      const raw = (opt.textContent ?? '').replace(/\s+/g, ' ').trim();
      const cleaned = raw.replace(/^Search\s+/i, '').replace(/^\[\s*|\s*\]$/g, '').trim();
      if (cleaned) slot.name = cleaned;
    }
  }

  // Per-category counts: each `<div class="fp">` contains the count in its
  // first `<i>` child or in a sibling, depending on layout version.
  const blocks = Array.from(doc.querySelectorAll('div.nosel div.fp, div.fp'));
  let idx = 0;
  for (const block of blocks) {
    if (idx > 9) break;
    const text = (block.textContent ?? '').trim();
    const numMatch = /(\d[\d,]*)/.exec(text);
    if (numMatch && numMatch[1]) {
      const n = Number.parseInt(numMatch[1].replace(/,/g, ''), 10);
      const slot = result.categories[idx];
      if (slot && Number.isFinite(n)) slot.count = n;
    }
    // If we didn't capture a name via <select>, try here too.
    const slot = result.categories[idx];
    if (slot && slot.name === DEFAULT_NAMES[idx]) {
      const nameMatch = /\d[\d,]*\s*(?:\(\s*\)\s*)?\s+(.+)$/.exec(text);
      const candidate = nameMatch?.[1]?.trim();
      if (candidate) slot.name = candidate;
    }
    idx++;
  }

  return result;
}
